import { html, LitElement, css, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {createRef, ref, Ref} from 'lit/directives/ref.js';

import { styles as sharedStyles } from '../../styles/shared-styles'

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';
import { RouterLocation } from '@vaadin/router';
import SimplePeer, { Instance } from 'simple-peer';
import { guard } from "lit-html/directives/guard.js";

@customElement('app-listener')
export class AppListener extends LitElement {
  @state() chanuuid: string = "";
  @state() peer: Instance | null = null;
  @state() key: CryptoKey | null = null;
  @state() encKey: number[] | null = null;
  @state() stream: MediaStream | null = null;
  hsId: string = "";
  playing: boolean = false;
  @state() muted: boolean = false;
  @state() status: string = "Connecting";
  audioRef: Ref<HTMLAudioElement> = createRef();
  silentAudioRef: Ref<HTMLAudioElement> = createRef();

  static styles = [
    sharedStyles,
    css`
      #audio-icon {
        font-size: 51vw;
      }
      @media(min-width: 520px) {
        #audio-icon {
          font-size: 20rem;
        }
      }
    `
  ]

  async onBeforeEnter(location: RouterLocation) {
    this.chanuuid = location.params.channel as string;

    await this.generateKeys();

    this.startWebRTC();
  }

  constructor() {
    super();
  }

  firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    navigator.mediaSession.setActionHandler('play', async () => {
      await this.audioRef.value?.play();
      await this.silentAudioRef.value?.play();
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      this.audioRef.value?.pause();
      this.silentAudioRef.value?.pause();
    });

    this.audioRef.value?.addEventListener('play', function() {
      navigator.mediaSession.playbackState = 'playing';
    });

    this.audioRef.value?.addEventListener('pause', function() {
      navigator.mediaSession.playbackState = 'paused';
    });
  }

  async generateKeys() {
    this.key = await window.crypto.subtle.generateKey(
      {
        name: "AES-GCM",
        length: 256
      },
      true,
      ["encrypt", "decrypt"]
    );

    let pbKey: JsonWebKey = await this.getStreamKey() as JsonWebKey;

    let bKey = await window.crypto.subtle.importKey(
      "jwk",
      pbKey,
      {
        name: "RSA-OAEP",
        hash: "SHA-256"
      },
      true,
      ["encrypt"]
    );

    await window.crypto.subtle.exportKey('jwk',this.key).then(async (rKey) => {
      this.encKey = await Array.from(new Uint8Array(await window.crypto.subtle.encrypt(
        {
          name: "RSA-OAEP"
        },
        bKey,
        new TextEncoder().encode(JSON.stringify(rKey))
      )));
    })


  }

  startWebRTC(): void {
    this.peer = new SimplePeer({
      trickle: false,
      initiator: true
    });
    this.peer.addTransceiver("audio", {direction: "recvonly"})

    this.peer.on('signal', async (data: SimplePeer.SignalData) => {
      fetch('/.netlify/functions/listener-handshake', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key: this.encKey,
          id: this.chanuuid,
          sdp: await this.encryptData(JSON.stringify(data)),
          hsId: this.hsId ? this.hsId : undefined
        })
      }).then((response) => response.json()).then((data: any) => {
        if(!this.hsId) {
          this.hsId = data.id;
          this.listenSocket(data.socket);
        }
        return;
      });
    });

    this.peer.on('data', (data: SimplePeer.SimplePeerData) => {
      let jData = JSON.parse(data as string);
      if(jData.type === "mute") {
        this.muted = jData.value;
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
              title: 'Livestream' + (this.muted ? ' - Host Muted' : ''),
              artist: 'trono.stream',  // You can set an artist or app name here
              artwork: [
                  { src: '/assets/icons/48x48.png', sizes: '48x48', type: 'image/jpeg' },
                  { src: '/assets/icons/192x192.png', sizes: '192x192', type: 'image/jpeg' },
                  { src: '/assets/icons/512x512.png', sizes: '512x512', type: 'image/jpeg' }
              ]
          });
      }
      }
      this.requestUpdate();
    });

    this.peer.on('stream', (data: MediaStream) => {
      this.stream = data;
      console.debug("STREAM: " + data)
      this.status = "Live"

    });

    this.peer.on('connect', () => {
      if(!this.stream){
        this.status = "Waiting for broadcast to start"
      }
      console.debug('connected to peer')
    });
  }

  async encryptData(data: string) : Promise<{iv: number[], data: number[]}> {
    let iv = window.crypto.getRandomValues(new Uint8Array(12));
    let eData = new TextEncoder().encode(data);
    let buffer = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv
      },
      this.key!,
      eData
    );
    return Promise.resolve({iv: Array.from(iv), data: Array.from(new Uint8Array(buffer))});
  }

  getStreamKey(): Promise<string> {
    return fetch('/.netlify/functions/get-key', {method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: this.chanuuid,
    })}).then(resp => resp.json())
  }

  listenSocket(socket: string): void {
    var key = 'fySCMw.J8cX4Q:961UZSb7JKCNqJHa0Gi3S2VHe2JOWXVLJ0BGYhFJgog';
    var url = 'https://realtime.ably.io/event-stream?channels=' + socket + '&v=1.1&key=' + key;
    var eventSource = new EventSource(url);

    eventSource.onmessage = async (event) => {
      var message = JSON.parse(JSON.parse(event.data).data);
      let sdp = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: new Uint8Array(message.iv)
        },
        this.key!,
        new Uint8Array(message.data).buffer
      );
      let sdpMessage = JSON.parse(new TextDecoder().decode(sdp));
      this.peer!.signal(sdpMessage);
    };
  }

  getAudioPlayer() {
    if(this.stream) {
      return html`
        ${this.muted ? html`<b>Host has muted their mic</b><br/><br/>` : html``}
        <audio ${ref(this.audioRef)} style="display: none" id="player" .srcObject=${guard(this.stream, () => this.stream)}></audio>
        <audio ${ref(this.silentAudioRef)} style="display: none" id="silentAudio" loop>
          <source src="/assets/silence.mp3" type="audio/mpeg">
            Your browser does not support the audio element.
        </audio>
        <sl-icon id='audio-icon' @click="${this.play}" name="play-circle-fill"></sl-icon>
      `
    } else {
      return html`<sl-progress-bar indeterminate></sl-progress-bar>`;
    }
  }

  play() {
    let aplayer: HTMLAudioElement = this.shadowRoot?.getElementById("player") as HTMLAudioElement;
    let pplayer: HTMLAudioElement = this.shadowRoot?.getElementById("silentAudio") as HTMLAudioElement;
    pplayer!.play();
    let aicon = this.shadowRoot!.getElementById("audio-icon");
    if(this.playing) {
      aplayer!.pause();
      aicon!.setAttribute("name", "play-circle-fill");
    } else {
      aplayer!.play();
      aicon!.setAttribute("name", "stop-circle-fill");

      // Use the Media Session API to set the media metadata
      if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
              title: 'Livestream' + (this.playing ? ' - Paused' : ''),
              artist: 'trono.stream',  // You can set an artist or app name here
              artwork: [
                  { src: '/assets/icons/48x48.png', sizes: '48x48', type: 'image/jpeg' },
                  { src: '/assets/icons/192x192.png', sizes: '192x192', type: 'image/jpeg' },
                  { src: '/assets/icons/512x512.png', sizes: '512x512', type: 'image/jpeg' }
              ]
          });
      }
      if ('setPositionState' in navigator.mediaSession) {
        navigator.mediaSession.setPositionState({
          duration: Infinity
        });
      }
    }
    this.playing = !this.playing;
  }

  render() {
    return html`
    <main>
      <sl-card class="listen-card">
        Listening to ${this.chanuuid}
        <br/>
        <br/>
        ${this.getAudioPlayer()}
        <br/><br/>
        ${this.status}
      </sl-card>
    </main>
    `;
  }
}
